import React, { useState, useEffect } from 'react';
import { api } from '../apiClient';

const SchedulerStatusPage = () => {
  const [status, setStatus] = useState(null);
  const [retries, setRetries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [retriesLoading, setRetriesLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchStatus = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/scheduler-status');
      setStatus(response.data);
    } catch (err) {
      setError('Failed to fetch scheduler status. You might not have the required permissions.');
      console.error(err);
    }
    setLoading(false);
  };

  const fetchRetries = async () => {
    setRetriesLoading(true);
    setError(null);
    try {
      const response = await api.get('/admin/mockup-retries');
      setRetries(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setError('Failed to fetch pending retries. You might not have the required permissions.');
      console.error(err);
    }
    setRetriesLoading(false);
  };

  const triggerScheduler = async () => {
    setLoading(true);
    setError(null);
    try {
      await api.post('/trigger-scheduler');
      setTimeout(() => {
        fetchStatus();
        fetchRetries();
      }, 1000);
    } catch (err) {
      setError('Failed to trigger scheduler. You might not have the required permissions.');
      console.error(err);
    }
    setLoading(false);
  };

  const cancelRetries = async (payload) => {
    setRetriesLoading(true);
    setError(null);
    try {
      await api.post('/admin/mockup-retries/cancel', payload);
      await fetchRetries();
      await fetchStatus();
    } catch (err) {
      setError('Failed to cancel retries. You might not have the required permissions.');
      console.error(err);
    }
    setRetriesLoading(false);
  };

  const cancelOlderThan = (days) => () => cancelRetries({ older_than_days: days });
  const cancelAll = () => cancelRetries({ older_than_days: 0 });

  useEffect(() => {
    fetchStatus();
    fetchRetries();
  }, []);

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-2xl font-bold mb-4">Scheduler Status</h1>
      {loading && <p>Loading...</p>}
      {error && <p className="text-red-500">{error}</p>}
      {status && (
        <div className="bg-white shadow-md rounded-lg p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="font-semibold">Last Run Time:</p>
              <p>{status.last_run_time ? new Date(status.last_run_time).toLocaleString() : 'N/A'}</p>
            </div>
            <div>
              <p className="font-semibold">Next Run Time:</p>
              <p>{status.next_run_time ? new Date(status.next_run_time).toLocaleString() : 'N/A'}</p>
            </div>
            <div>
              <p className="font-semibold">Currently Processing:</p>
              <p>{status.currently_processing?.length > 0 ? status.currently_processing.join(', ') : 'None'}</p>
            </div>
            {status.max_retry_age_days != null && status.max_retry_age_days > 0 && (
              <div>
                <p className="font-semibold">Max retry age (days):</p>
                <p>Retries older than {status.max_retry_age_days} days are auto-expired</p>
              </div>
            )}
          </div>
          <div className="mt-6 flex flex-wrap gap-4">
            <button
              onClick={fetchStatus}
              className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
              disabled={loading}
            >
              Refresh Status
            </button>
            <button
              onClick={triggerScheduler}
              className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded"
              disabled={loading}
            >
              Trigger Scheduler Manually
            </button>
          </div>
        </div>
      )}

      <div className="bg-white shadow-md rounded-lg p-6">
        <h2 className="text-xl font-bold mb-4">Pending Mockup Retries</h2>
        <p className="text-gray-600 text-sm mb-4">
          These mockups are scheduled or currently retrying. Cancel expired or unwanted retries to stop background checks.
        </p>
        {retriesLoading && <p>Loading retries...</p>}
        {!retriesLoading && retries.length === 0 && (
          <p className="text-gray-500">No pending retries.</p>
        )}
        {!retriesLoading && retries.length > 0 && (
          <>
            <div className="overflow-x-auto mb-4">
              <table className="min-w-full border border-gray-200">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-4 py-2 text-left">ID</th>
                    <th className="px-4 py-2 text-left">User ID</th>
                    <th className="px-4 py-2 text-left">Created</th>
                    <th className="px-4 py-2 text-left">Next retry at</th>
                    <th className="px-4 py-2 text-left">Retry count</th>
                    <th className="px-4 py-2 text-left">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {retries.map((r) => (
                    <tr key={r.id} className="border-t border-gray-200">
                      <td className="px-4 py-2">{r.id}</td>
                      <td className="px-4 py-2 font-mono text-sm">{r.user_id}</td>
                      <td className="px-4 py-2">{r.created_at ? new Date(r.created_at).toLocaleString() : '—'}</td>
                      <td className="px-4 py-2">{r.next_retry_at ? new Date(r.next_retry_at).toLocaleString() : '—'}</td>
                      <td className="px-4 py-2">{r.retry_count}</td>
                      <td className="px-4 py-2">{r.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex flex-wrap gap-4">
              <button
                onClick={fetchRetries}
                className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                disabled={retriesLoading}
              >
                Refresh list
              </button>
              <button
                onClick={cancelOlderThan(7)}
                className="bg-amber-500 hover:bg-amber-600 text-white font-bold py-2 px-4 rounded"
                disabled={retriesLoading}
              >
                Cancel retries older than 7 days
              </button>
              <button
                onClick={cancelAll}
                className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded"
                disabled={retriesLoading}
              >
                Cancel all pending retries
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SchedulerStatusPage;
